import { useEffect, useState } from "react"
import { isTechLive, TechClockState, TechDeviceState, useTechClock, useTechDeviceState } from "../../core/TechLive"
import Icon from "../base/Icon"
import { batteryIcon, batteryTone, currentBattery } from "./TechBatteryLabel"
import { appStateWords, locationWords, platformName, reportedAt } from "./TechDeviceWords"
import { techLocationFreshness } from "./TechLocationFreshness"

interface IProps {
    techId?: string
    /** When their phone last sent a position — one of the things "Updated" is measured from. */
    locationAt?: Date | string
}

/**
 * Everything the technician's phone says about itself, in one card: whether the app is open,
 * its battery, what it allows with location, and whether they are on the clock — under one
 * "Updated" that is the last time any of it changed, because the phone reports it all together.
 */
export default function TechPhoneStatusCard(props: IProps) {

    const state = useTechDeviceState(props.techId)
    const clock = useTechClock(props.techId)

    // "Just now" turns into "1 minute ago" without anything new arriving.
    const [, tickSet] = useState(0)
    useEffect(() => {
        const timer = window.setInterval(() => tickSet(t => t + 1), 30000)
        return () => window.clearInterval(timer)
    }, [])

    const updated = latestOf(state, clock, props.locationAt)
    const live = isTechLive(props.techId, props.locationAt)
    const hero = heroFor(state, clock)
    const platform = platformName(state?.platform)

    return (
        <div className="tech-card tech-phone-card mb-4">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="tech-card-label">
                    {platform &&
                        <Icon icon={platform === "iPhone" ? "apple" : "android"} type="brands" size={11} className="me-2" />
                    }
                    {platform ?? "Phone"}
                </div>
                {/* When any of this last changed. */}
                <div className="d-inline-flex align-items-center fs-8 text-gray-500"
                    title={updated ? `Updated ${updated.toLocaleString()}` : undefined}>
                    {/* Live, the breathing dot says it; otherwise the clock says this is a time. */}
                    {live
                        ? <span className="tech-live-dot me-2" />
                        : <Icon icon="clock" size={11} className="me-1" />}
                    {updated ? techLocationFreshness(updated).label : "Never reported"}
                </div>
            </div>

            <div className="d-flex align-items-center">
                <div className={`tech-hero-icon bg-light-${hero.tone === "gray-500" ? "secondary" : hero.tone} text-${hero.tone}`}>
                    <Icon icon={hero.icon} type="solid" size={18} />
                </div>
                <div style={{ minWidth: 0 }}>
                    <div className={`fs-4 fw-bold text-${hero.tone === "gray-500" ? "gray-700" : hero.tone} lh-sm`}>{hero.label}</div>
                    <div className="fs-8 text-gray-500 mt-1">{hero.note}</div>
                </div>
            </div>

            <div className="tech-stats mt-5">
                <BatteryStat state={state} clock={clock} />
                <NetworkStat state={state} />
                <LocationStat state={state} />
                <ShiftStat clock={clock} />
            </div>
        </div>
    )
}

function BatteryStat(props: { state?: TechDeviceState, clock?: TechClockState }) {
    const current = currentBattery(props.state, props.clock?.on)
    const level = current?.level ?? props.state?.batteryLevel

    if (level === undefined || level === null) {
        return <Stat icon="battery-empty" tone="gray-400" value="—" caption="Battery" />
    }

    // Kept but greyed once it says what it was rather than what it is.
    const tone = current ? batteryTone(current.level, current.charging) : "gray-500"

    return (
        <Stat
            icon={current?.charging ? "battery-bolt" : batteryIcon(level)}
            tone={tone}
            value={`${level}%`}
            caption={current?.charging ? "Charging" : "Battery"}
        />
    )
}

/** Wi-Fi or mobile, which generation, and the bars where the phone says them. */
function NetworkStat(props: { state?: TechDeviceState }) {
    const type = props.state?.networkType
    const signal = props.state?.networkSignal

    if (!type) return <Stat icon="signal" tone="gray-400" value="—" caption="Network" />

    if (type === "none") {
        return <Stat icon="signal-slash" tone="danger" value="Offline" caption="No network" />
    }

    const value = type === "wifi" ? "Wi-Fi"
        : type === "cellular" ? (props.state?.networkGeneration ?? "Mobile")
            : "Online"

    const caption = type === "wifi" ? "Wi-Fi"
        : type === "cellular" ? "Mobile data"
            : "Network"

    // Bars where the phone says them; weak is worth a colour of its own.
    const tone = signal === undefined || signal === null ? "gray-700"
        : signal <= 1 ? "danger"
            : signal === 2 ? "warning"
                : "success"

    return (
        <div className="tech-stat" title={signal === undefined || signal === null ? "This phone does not report signal strength." : `${signal} of 4 bars`}>
            <div className={`d-flex align-items-center fw-bold fs-6 text-${tone}`}>
                {signal === undefined || signal === null
                    ? <Icon icon={type === "wifi" ? "wifi" : "signal"} type="solid" size={13} className="me-2" />
                    : <SignalBars bars={signal} wifi={type === "wifi"} />}
                <span className="text-truncate">{value}</span>
            </div>
            <div className="fs-8 text-gray-500 mt-1 text-truncate">{caption}</div>
        </div>
    )
}

/** Four rising bars, as many filled as the phone has — or a Wi-Fi fan for Wi-Fi. */
function SignalBars(props: { bars: number, wifi?: boolean }) {
    if (props.wifi) {
        // Three arcs and a dot, lit from the dot outwards.
        const lit = (n: number) => props.bars >= n ? "currentColor" : "var(--bs-gray-300)"
        return (
            <svg className="me-2 flex-shrink-0" width="16" height="13" viewBox="0 0 16 13" aria-hidden="true">
                <path d="M1 4.5a10 10 0 0 1 14 0" stroke={lit(4)} strokeWidth="1.8" fill="none" strokeLinecap="round" />
                <path d="M3.5 7a6.4 6.4 0 0 1 9 0" stroke={lit(3)} strokeWidth="1.8" fill="none" strokeLinecap="round" />
                <path d="M6 9.5a2.8 2.8 0 0 1 4 0" stroke={lit(2)} strokeWidth="1.8" fill="none" strokeLinecap="round" />
                <circle cx="8" cy="11.6" r="1.1" fill={lit(1)} />
            </svg>
        )
    }

    return (
        <span className="tech-signal-bars me-2 flex-shrink-0" aria-hidden="true">
            {[1, 2, 3, 4].map(n => <span key={n} className={props.bars >= n ? "on" : ""} style={{ height: 3 + n * 2.5 }} />)}
        </span>
    )
}

function LocationStat(props: { state?: TechDeviceState }) {
    const word = props.state?.locationStatus ? locationWords[props.state.locationStatus] : undefined
    const approximate = props.state?.locationPrecise === false && props.state?.locationStatus !== "denied"

    return (
        <Stat
            icon="location-crosshairs"
            tone={word?.tone ?? "gray-400"}
            value={word?.label ?? "—"}
            caption={approximate ? "Approximate" : "Location"}
            title={word?.note}
        />
    )
}

function ShiftStat(props: { clock?: TechClockState }) {
    if (!props.clock) return <Stat icon="clock" tone="gray-400" value="—" caption="Shift" />

    const since = props.clock.at
        ? props.clock.at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
        : undefined

    return (
        <Stat
            icon="clock"
            tone={props.clock.on ? "success" : "gray-500"}
            value={props.clock.on ? "On clock" : "Off clock"}
            caption={since ? `Since ${since}` : "Shift"}
        />
    )
}

function Stat(props: { icon: any, tone: string, value: string, caption: string, title?: string }) {
    return (
        <div className="tech-stat" title={props.title}>
            <div className={`d-flex align-items-center fw-bold fs-6 text-${props.tone === "gray-400" ? "gray-500" : props.tone}`}>
                <Icon icon={props.icon} type="solid" size={13} className="me-2" />
                <span className="text-truncate">{props.value}</span>
            </div>
            <div className="fs-8 text-gray-500 mt-1 text-truncate">{props.caption}</div>
        </div>
    )
}

interface Hero {
    icon: any
    tone: string
    label: string
    note: string
}

/** The big line: how the app is running — or, off the clock, that it is not meant to be reporting. */
function heroFor(state?: TechDeviceState, clock?: TechClockState): Hero {
    if (clock && !clock.on) {
        return { icon: "moon", tone: "gray-500", label: "Off the clock", note: "The app does not report outside a shift." }
    }

    const word = state?.appState ? appStateWords[state.appState] : undefined

    switch (state?.appState) {
        case "foreground": return { icon: "mobile-screen-button", tone: word!.tone, label: word!.label, note: word!.note }
        case "background": return { icon: "mobile-screen", tone: word!.tone, label: word!.label, note: word!.note }
        case "closed": return { icon: "power-off", tone: word!.tone, label: word!.label, note: word!.note }
    }

    return { icon: "mobile-screen", tone: "gray-500", label: "App state not reported", note: "This app version does not say whether it is open." }
}

/** The last time anything on the card changed: the phone's report, its battery, a position, the clock. */
function latestOf(state?: TechDeviceState, clock?: TechClockState, locationAt?: Date | string): Date | undefined {
    const times = [
        reportedAt(state?.reportedAt),
        reportedAt(state?.batteryAt),
        reportedAt(state?.appStateAt),
        reportedAt(state?.networkAt),
        locationAt ? new Date(locationAt) : undefined,
        clock?.at
    ]
        .map(t => t?.getTime())
        .filter((t): t is number => t !== undefined && !isNaN(t))

    return times.length ? new Date(Math.max(...times)) : undefined
}
